import { createSelector } from "@reduxjs/toolkit";

import type { EnvTypeDataRetention } from ".";
import type { RootState } from "Store/configureStore";
import {
  selectImagesLifecycleManagementFeatureEnabled,
  selectOCIDockerFeatureEnabled
} from "Reducers/featureFlags/featureFlags.selectors";

export const projectSettingsStateSelector = (state: RootState) =>
  state.projectSettings;

export const projectSettingsLoadingSelector = createSelector(
  projectSettingsStateSelector,
  projectSettings => projectSettings.loading
);

export const projectSettingsErrorSelector = createSelector(
  projectSettingsStateSelector,
  projectSettings => {
    const error = projectSettings.error;
    if (typeof error?.detail === "string") return error.detail;
    if (typeof error?.detail === "object") {
      return Object.values(error.detail)[0];
    }
    if (error?.message) return error.message;
  }
);

export const projectSettingsSelector = createSelector(
  projectSettingsStateSelector,
  (_: RootState, params: { projectId: string }) => params,
  (projectSettings, { projectId }) => projectSettings?.data?.[projectId]
);

export const projectSettingsSizingApiSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.sizing_api_enabled
);

export const buildResourcesProjectSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.build_resources
);

export const dataRetentionSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.data_retention
);

export const envDataRetentionSelector = createSelector(
  dataRetentionSelector,
  (_: RootState, params: { environmentType: EnvTypeDataRetention }) => params,
  (dataRetention, { environmentType }) => dataRetention?.[environmentType]
);

const generateFrequencyAndBackupsCount = (scheduleData, manualCount = 0) => {
  const frequencyOptions = {
    h: { show: false, label: "Hourly" },
    d: { show: false, label: "Daily" },
    w: { show: false, label: "Weekly" },
    M: { show: false, label: "Monthly" },
    y: { show: false, label: "Yearly" }
  };
  let backupsCount = 0;

  scheduleData.forEach(schedule => {
    backupsCount += schedule.count;
    const freqChar = schedule.interval.replace(/\d*/, "");
    if (frequencyOptions?.[freqChar]) {
      frequencyOptions[freqChar].show = true;
    }
  });

  const cleanFrequency = Object.values(frequencyOptions)
    .filter(option => option.show)
    .map(option => option.label)
    .join(", ");

  return [cleanFrequency, backupsCount + manualCount] as [string, number];
};

export const backupEnvTypesInfoSelector = createSelector(
  dataRetentionSelector,
  data => {
    const prod = { type: "production", frequency: "", backups: 0 };
    const staging = { type: "staging", frequency: "", backups: 0 };
    const dev = { type: "development", frequency: "", backups: 0 };

    return [prod, staging, dev].map(backup => {
      if (data?.[backup.type]) {
        const [frequency, backupsCount] = generateFrequencyAndBackupsCount(
          data?.[backup.type]?.default_config?.schedule,
          data?.[backup.type]?.default_config?.manual_count
        );

        backup.frequency = frequency;
        backup.backups = backupsCount;
      }
      return backup;
    });
  }
);

export const projectSettingsManualDeploymentSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.allow_manual_deployments
);

export const allowRollingDeploymentSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.allow_rolling_deployments
);

export const maintenanceWindowProjectSelector = createSelector(
  projectSettingsSelector,
  projectSetting => projectSetting?.maintenance_window
);

export const canSetMaintenanceWindowSelector = createSelector(
  maintenanceWindowProjectSelector,
  maintenanceWindow => maintenanceWindow?.enabled || maintenanceWindow === null
);

export const hasMaintenanceWindowDetailsSelector = createSelector(
  maintenanceWindowProjectSelector,
  maintenanceWindow =>
    Boolean(
      maintenanceWindow?.recurrence?.interval &&
      maintenanceWindow?.recurrence?.day_of_week !== undefined &&
      maintenanceWindow?.recurrence?.time
    )
);

export const supportOCIImagesSelector = createSelector(
  projectSettingsSelector,
  selectOCIDockerFeatureEnabled,
  (projectSetting, isOCIDockerFeatureEnabled) => {
    return Boolean(
      projectSetting?.support_oci_images && isOCIDockerFeatureEnabled
    );
  }
);

export const supportImageLifecycleManagementSelector = createSelector(
  projectSettingsSelector,
  selectImagesLifecycleManagementFeatureEnabled,
  (projectSetting, isImagesLifecycleManagementEnabled) =>
    Boolean(
      projectSetting?.image_lifecycle?.enabled &&
      isImagesLifecycleManagementEnabled
    )
);
